<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Plan } from '../../views/SettingBillingView/usePlan';
import type { Order } from '../../views/SettingBillingView/SettingBillingView.vue';
import { Child } from 'src/modules/Child/models/Child';
import { useRepo } from 'pinia-orm';
import {
  mdiCloseCircleOutline,
  mdiRepeat,
  mdiRepeatOff,
} from '@quasar/extras/mdi-v7';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';

const $q = useQuasar();

interface Props {
  order: Order;
  plan: Plan | undefined;
}
const props = defineProps<Props>();

const childRepo = useRepo(Child);

const formattedCreated = computed(() =>
  // Format as short readable date 22/10/2025
  props.order.createdAt
    ? new Date(props.order.createdAt).toLocaleDateString(['en-GB', 'uk-UA'], {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '',
);

const periodPriceString = computed(() => {
  switch (props.plan?.period) {
    case 'day':
      return 'DAILY';
    case 'week':
      return 'WEEKLY';
    case 'month':
      return 'MONTHLY';
    case 'year':
      return 'YEARLY';
    default:
      return 'UNKNOWN';
  }
});

const childName = computed(() => {
  // Find child from child repository
  const child = childRepo.find(props.order.child_id);
  return child ? child.fullname : '';
});

const priceString = computed(() => {
  const price = props.order.amount;
  return `${price} ${props.order.currency}`;
});

const emit = defineEmits<{
  'order:unsubscribe': [string];
}>();

const disableUnsubscribe = ref(false);
function handleUnsubscribe(orderId: string) {
  // confirmation modal
  $q.dialog({
    title: t('CONFIRM_UNSUBSCRIBE'),
    message: t('ARE_YOU_SURE_YOU_WANT_TO_UNSUBSCRIBE'),
    dark: true,
    ok: {
      label: t('YES'),
      color: 'negative',
    },
    cancel: {
      color: 'border',
      label: t('NO'),
    },
  }).onOk(() => {
    disableUnsubscribe.value = true;
    emit('order:unsubscribe', orderId);
  });
}
</script>

<template>
  <q-card
    flat
    bordered
    class="full-width"
    style="max-width: 600px"
  >
    <q-card-section class="q-pa-none">
      <q-list
        dark
        bordered
      >
        <q-item>
          <q-item-section class="text-subtitle1 text-bold">
            <q-item-label>
              {{ childName || '' }}
            </q-item-label>
            <q-item-label
              caption
              class="text-muted"
            >
              {{ $t(periodPriceString) }}
              {{ priceString }}
            </q-item-label>
          </q-item-section>

          <q-item-section side>
            <q-item-label>
              <q-icon
                :name="
                  order?.action_status === 'subscribed'
                    ? mdiRepeat
                    : mdiRepeatOff
                "
                :color="
                  order?.action_status === 'subscribed'
                    ? 'positive'
                    : 'negative'
                "
              />
              {{ order?.action_status || '' }}
            </q-item-label>
            <q-item-label caption>
              <q-chip
                v-if="order?.createdAt"
                class="q-ml-sm"
                color="brand"
                outline
                dense
                size="sm"
                :label="formattedCreated"
              />
            </q-item-label>
          </q-item-section>

          <q-item-section side>
            <!-- Context menu button -->

            <q-btn
              color="negative"
              flat
              size="md"
              :disable="disableUnsubscribe"
              :title="$t('UNSUBSCRIBE')"
              :icon="mdiCloseCircleOutline"
              @click="handleUnsubscribe(order.order_id)"
            />
          </q-item-section>
        </q-item>
      </q-list>
    </q-card-section>
  </q-card>
</template>
